import { computed, nextTick, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

import { useChallenges } from '@/api/challenges/use-challenges';
import { useEvents } from '@/api/events/use-events';
import { useUgcs } from '@/api/ugcs/use-ugcs';
import { useCommonStore } from '@/stores/common';

export type DeepLinkActionType = 'challenge' | 'event' | 'ugc';
export type DeepLinkBucket = 'available' | 'ongoing' | 'validated' | 'rejected';

const QUERY_PARAM: Record<DeepLinkActionType, string> = {
  challenge: 'challengeId',
  event: 'eventId',
  ugc: 'ugcId',
};

const ID_FIELD: Record<DeepLinkActionType, string> = {
  challenge: 'challengeId',
  event: 'eventId',
  ugc: 'ugcId',
};

const ROUTE_NAMES: Record<
  DeepLinkActionType,
  Record<DeepLinkBucket, string>
> = {
  challenge: {
    available: 'available-challenges',
    ongoing: 'ongoing-challenges',
    validated: 'validated-challenges',
    rejected: 'rejected-challenges',
  },
  event: {
    available: 'available-events',
    ongoing: 'ongoing-events',
    validated: 'validated-events',
    rejected: 'rejected-events',
  },
  ugc: {
    available: 'available-ugcs',
    ongoing: 'ongoing-ugcs',
    validated: 'validated-ugcs',
    rejected: 'rejected-ugcs',
  },
};

const BUCKETS: DeepLinkBucket[] = [
  'available',
  'ongoing',
  'validated',
  'rejected',
];

/**
 * Opens one specific action's detail modal from a `?challengeId=` / `?eventId=` /
 * `?ugcId=` query param — the deep link used by admin share links and by the
 * launch / re-engagement notifications.
 *
 * Each list view only renders the actions of its own bucket, so the composable
 * first resolves which bucket holds the id: if it is this view's, it opens the
 * modal; if not, it hops to the matching tab and the view mounted there takes
 * over. The param is stripped once consumed so a refresh (or closing the modal)
 * does not reopen it.
 */
export const useActionDeepLink = <T extends object = Record<string, unknown>>({type,
  bucket,
  onOpen}: {
  type: DeepLinkActionType;
  bucket: DeepLinkBucket;
  /** Opens this view's detail modal. `id` saves simple views a cast. */
  onOpen: (item: T, id: number) => void;
}) => {
  const route = useRoute();
  const router = useRouter();
  const commonStore = useCommonStore();
  const { t } = useI18n({ useScope: 'global' });

  // Same query key and `staleTime: Infinity` as the view itself, so this reuses
  // the cache entry instead of issuing another request.
  const query =
    type === 'challenge'
      ? useChallenges()
      : type === 'event'
        ? useEvents()
        : useUgcs();

  let consumed = false;

  const targetId = computed(() => {
    const raw = route.query[QUERY_PARAM[type]];
    const value = Number(Array.isArray(raw) ? raw[0] : raw);
    return Number.isInteger(value) && value > 0 ? value : null;
  });

  const idOf = (item: T) =>
    (item as Record<string, unknown>)[ID_FIELD[type]] as number | undefined;

  const clearParam = () => {
    const query = { ...route.query };
    delete query[QUERY_PARAM[type]];
    // `replace` so the browser Back button does not bounce into the modal.
    router.replace({
      path: route.path,
      query: Object.keys(query).length ? query : undefined,
    });
  };

  watch(
    [
      targetId,
      () => query.isPending.value,
      () => query.isError.value,
      () => query.data.value,
    ],
    () => {
      const id = targetId.value;
      if (consumed || !id) return;

      // Wait for the buckets: a cold load has no data to resolve the id against yet.
      if (query.isPending.value) return;

      if (query.isError.value) {
        // http.ts already surfaces request failures; don't double-toast.
        // Keep the target so retrying or refreshing can still resolve it.
        return;
      }

      const data = query.data.value as
        | Record<DeepLinkBucket, T[] | undefined>
        | undefined;

      const foundBucket = BUCKETS.find((candidate) =>
        (data?.[candidate] ?? []).some((item) => idOf(item) === id),
      );

      if (!foundBucket) {
        consumed = true;
        commonStore.setAlert({
          open: true,
          type: 'error',
          title: t('actionDeepLink.notFound.title'),
          description: t(`actionDeepLink.notFound.${type}`),
          duration: 5000,
        });
        clearParam();
        return;
      }

      consumed = true;

      if (foundBucket !== bucket) {
        // Forward the query explicitly: the view rendered by the target tab runs
        // this same composable and opens the modal there.
        router.replace({
          name: ROUTE_NAMES[type][foundBucket],
          query: { ...route.query },
        });
        return;
      }

      const item = (data?.[bucket] ?? []).find(
        (candidate) => idOf(candidate) === id,
      );
      if (!item) return;

      // The modal lives inside the list's v-for, so let it render first.
      nextTick(() => {
        onOpen(item, id);
        clearParam();
      });
    },
    { immediate: true },
  );
};
